/* eslint-disable jsx-a11y/alt-text */
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useDispatch } from 'react-redux';
import { getErrorMessage } from 'utils/index';
import { showToast } from 'utils/helpers';
import { setCountNotRead, setTypeMail } from './actions/index';
import { createOrUpdateMailBox, deleteForceMail, deleteMailSelected, getListMailDraft, getListMailFlagged, getListMailInbox, getListMailNotRead, getListMailSend, getListMailTrash } from './helpers/call-api';
import MailFilter from './MailFilter';
import MailTable from './MailTable';
import MailMenu from './Tab/MailMenu';
import { useHistory, useLocation } from 'react-router-dom';
import { buildMailReturnPath, getMailPageFromLocation, updateMailPageSearch } from './utils/mailNavigation';

const Mail = ({ typeMail = "inbox" , hiddenMenu, noPaging }) => {

    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation()
    const requestIdRef = useRef(0)
    const mailListPath = typeMail === 'inbox' ? '/mail' : `/mail/${typeMail}`
    const isMailListRoute = location.pathname === mailListPath

    const [params, setParams] = useState(() => ({
        page: getMailPageFromLocation(location),
        itemsPerPage: 50,
        is_active: 1,
        keyword: location.state?.mailParams?.keyword || '',
    }));

    const [dataList, setDataList] = useState({
        items: [],
        itemsPerPage: 0,
        page: 0,
        totalItems: 0,
        totalPages: 0,
    });

    const [loading, setLoading] = useState(true);
    const { items, itemsPerPage, page, totalItems } = dataList;

    const getData = useCallback(async () => {
        const requestId = ++requestIdRef.current;
        setLoading(true);
        const loader = {
            send: getListMailSend,
            flagged: getListMailFlagged,
            draft: getListMailDraft,
            trash: getListMailTrash,
            inbox: getListMailInbox,
        }[typeMail] || getListMailInbox;

        try {
            const value = await loader(params);
            if (requestId === requestIdRef.current) {
                setDataList(value);
            }
        } catch (error) {
            if (requestId === requestIdRef.current) {
                getErrorMessage(error);
            }
        } finally {
            if (requestId === requestIdRef.current) {
                setLoading(false);
            }
        }
    }, [params, typeMail]);
    useEffect(() => {
        getData();
        return () => {
            requestIdRef.current += 1;
        };
    }, [getData]);

    useEffect(() => {
        localStorage.setItem('typeMail', typeMail);
        dispatch(setTypeMail(typeMail));
    }, [dispatch, typeMail]);

    useEffect(() => {
        if (!isMailListRoute) return;

        const nextSearch = updateMailPageSearch(location.search, params.page);
        const currentMailParams = location.state?.mailParams;
        const hasCurrentMailParams = currentMailParams?.page === params.page
            && currentMailParams?.itemsPerPage === params.itemsPerPage
            && currentMailParams?.is_active === params.is_active
            && currentMailParams?.keyword === params.keyword;

        if (location.search === nextSearch && hasCurrentMailParams) return;

        history.replace({
            pathname: location.pathname,
            search: nextSearch,
            hash: location.hash,
            state: {
                ...location.state,
                mailParams: params,
            },
        });
    }, [history, isMailListRoute, location.hash, location.pathname, location.search, location.state, params]);

    const getCountNotRead = useCallback(() => {
        getListMailNotRead()
            .then((value) => {
                dispatch(setCountNotRead(value))
            })
            .catch(getErrorMessage)
    }, [dispatch])

    useEffect(() => {
        getCountNotRead()
    }, [getCountNotRead])

    const checkValueFlagged = (handleUpdateStatusFlagged, item, index) => {
        let cloneData = { ...item, is_flagged: !item.is_flagged }
        handleUpdateStatusFlagged(cloneData, index)
    }

    const handleUpdateStatusFlagged = async (value, index) => {
        try {
            await createOrUpdateMailBox(value)
            let dataMailClone = [...items]
            dataMailClone[index].is_flagged = value.is_flagged
            setDataList((prev) => ({
                ...prev,
                items: dataMailClone
            }))
        } catch (error) {
            getErrorMessage(error)
        }
    }

    const handleBulkFlagged = async (selectedMails) => {
        const mailsToFlag = selectedMails.filter((mail) => !mail?.is_flagged);
        if (!mailsToFlag.length) {
            showToast.info('Các thư đã chọn đều đã được đánh dấu sao.');
            return { successfulIds: selectedMails.map((mail) => mail.mail_id), failedIds: [] };
        }

        const results = await Promise.allSettled(
            mailsToFlag.map((mail) => createOrUpdateMailBox({ ...mail, is_flagged: 1 }))
        );
        const successfulIds = [];
        const failedIds = [];

        results.forEach((result, index) => {
            const mailId = mailsToFlag[index]?.mail_id;
            if (result.status === 'fulfilled') {
                successfulIds.push(mailId);
            } else {
                failedIds.push(mailId);
            }
        });

        if (successfulIds.length) {
            const successfulIdSet = new Set(successfulIds);
            setDataList((prev) => ({
                ...prev,
                items: prev.items.map((mail) => successfulIdSet.has(mail.mail_id)
                    ? { ...mail, is_flagged: 1 }
                    : mail),
            }));
        }

        if (failedIds.length) {
            showToast.warning(`Đã đánh dấu sao ${successfulIds.length}/${mailsToFlag.length} thư.`);
        } else {
            showToast.success(`Đã đánh dấu sao ${successfulIds.length} thư.`);
        }

        return { successfulIds, failedIds };
    }

    const handleBulkDelete = async (selectedMails) => {
        const selectedIds = selectedMails.map((mail) => mail.mail_id);
        let successfulIds = [];
        let failedIds = [];

        if (typeMail === 'trash') {
            const results = await Promise.allSettled(selectedIds.map((mailId) => deleteForceMail(mailId)));
            results.forEach((result, index) => {
                if (result.status === 'fulfilled') {
                    successfulIds.push(selectedIds[index]);
                } else {
                    failedIds.push(selectedIds[index]);
                }
            });
        } else {
            await deleteMailSelected(selectedMails);
            successfulIds = selectedIds;
        }

        if (successfulIds.length) {
            const successfulIdSet = new Set(successfulIds);
            const remainingItems = items.filter((mail) => !successfulIdSet.has(mail.mail_id));
            const nextPage = remainingItems.length === 0 && params.page > 1 ? params.page - 1 : params.page;

            if (nextPage !== params.page) {
                setParams((prev) => ({ ...prev, page: nextPage }));
            } else {
                await getData();
            }
            getCountNotRead();
        }

        if (failedIds.length) {
            showToast.warning(`Đã xóa ${successfulIds.length}/${selectedIds.length} thư.`);
        } else {
            showToast.success(typeMail === 'trash'
                ? `Đã xóa vĩnh viễn ${successfulIds.length} thư.`
                : `Đã chuyển ${successfulIds.length} thư vào thùng rác.`);
        }

        return { successfulIds, failedIds };
    }

    const handleChangeRouterView = async (value) => {
        try {
            const returnTo = buildMailReturnPath({
                pathname: mailListPath,
                search: isMailListRoute ? location.search : '',
                page: params.page,
            });
            const navigationState = { typeMail, mailParams: params, returnTo };
            if (value.is_draft) {
                history.push(`/mail/detail/${value?.mail_id}`, navigationState);
            } else {
                // let cloneData = { ...value, is_read: 1 };
                // console.log('---sadfasd',cloneData);
                // await createOrUpdateMailBox(cloneData)
                history.push(`/mail/detail/${value?.mail_id}`, navigationState)
            }
        } catch (error) {
            getErrorMessage(error)
        }
    }

    return (
        <div className={hiddenMenu ? '' : 'bw_main_wrapp'}>
            <div className='bw_row bw_mail_layout'>
                {!hiddenMenu && <MailMenu typeMail={typeMail} />}
                <div className={hiddenMenu ? 'bw_col_12' : 'bw_col_9 bw_mail_content_column'}>
                    {!hiddenMenu && (
                        <MailFilter
                            defaultKeyword={params.keyword}
                            onChange={(e) => {
                                setParams((prev) => {
                                    return {
                                        ...prev,
                                        ...e,
                                        page: 1,
                                    };
                                });
                            }}
                        />
                    )}
                    <MailTable
                        hiddenMenu={hiddenMenu}
                        noPaging={noPaging}                        
                        onChangePage={(page) => {
                            setParams({
                                ...params,
                                page,
                            });
                        }}
                        data={items}
                        totalPages={Math.max(1, Math.ceil((totalItems || 0) / (itemsPerPage || 50)))}
                        itemsPerPage={itemsPerPage}
                        page={Math.max(1, Number(page) || 1)}
                        totalItems={totalItems}
                        loading={loading}
                        handleUpdateStatusFlagged={handleUpdateStatusFlagged}
                        checkValueFlagged={checkValueFlagged}
                        setDataList={setDataList}
                        typeMail={typeMail}
                        handleChangeRouterView={handleChangeRouterView}
                        mailParams={params}
                        returnTo={buildMailReturnPath({
                            pathname: mailListPath,
                            search: isMailListRoute ? location.search : '',
                            page: params.page,
                        })}
                        onBulkFlagged={handleBulkFlagged}
                        onBulkDelete={handleBulkDelete}
                    />

                </div>
            </div>
        </div>
    )
}

export default Mail
